import { useMutation } from "@apollo/client";
import {
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  AdminEditUserMutation,
  AdminEditUserMutationVariables,
  UserFragment,
} from "gql/graphql";
import React, { memo, useCallback, useState } from "react";
import colors from "app/colors";
import ADMIN_EDIT_USER_MUTATION from "../_networking/admin-edit-user";
import {
  CancelIcon,
  CheckIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import { getMessageFromError, getUserFullName } from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import UserName from "modules/_shared/user-name";

interface IProps {
  user: UserFragment;
}

const AdminEditUserName = ({ user }: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const { mode, setMode } = useGlobalEditing(`AdminEditUserName-${user.id}`);

  const [adminEditUserMutationAPI] = useMutation<
    AdminEditUserMutation,
    AdminEditUserMutationVariables
  >(ADMIN_EDIT_USER_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    setValue,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
  });

  const onClearFirstNameClicked = useCallback(() => {
    setValue("firstName", "");
  }, [setValue]);

  const onClearLastNameClicked = useCallback(() => {
    setValue("lastName", "");
  }, [setValue]);

  const onSubmit = useCallback(
    async ({ firstName, lastName }: FieldValues) => {
      setIsSubmitting(true);
      adminEditUserMutationAPI({
        variables: {
          id: user.id,
          input: {
            firstName,
            lastName,
          },
        },
        onError: (error) => {
          setIsSubmitting(false);
          setError("firstName", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setIsSubmitting(false);
          setMode("display")();
        },
      });
    },
    [adminEditUserMutationAPI, setError, setMode, user.id],
  );

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-user-name-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.firstName} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="firstName"
                  size="sm"
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("firstName")}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear first name"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearFirstNameClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="lastName"
                  size="sm"
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("lastName")}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear last name"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearLastNameClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  isSubmitting ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                onClick={setMode("edit")}
                isDisabled={isSubmitting}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={isSubmitting}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.firstName?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  if (isEmpty(getUserFullName(user))) {
    return (
      <Button
        variant="ghost"
        size="xxs"
        fontSize={12}
        fontWeight={500}
        color={colors.gray40}
        onClick={setMode("edit")}
      >
        + Add First/Last Name
      </Button>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      _hover={{
        backgroundColor: colors.blue20,
      }}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      <Text fontFamily="Outfit" fontSize={26} fontWeight={700}>
        <UserName userId={user.id} />
      </Text>
      <Spacer />
      <IconButton
        aria-label="Edit user name"
        icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(AdminEditUserName);
